import React, { useState, useEffect } from "react"
import SpaceUI from "@/componentsUI/Space"
import InputUI from "@/componentsUI/Input"
import ButtonUI from "@/componentsUI/Button"
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus"
import classNames from "classnames"
import useDialogManager from "@/utils/useDialogManager"

type AmountShortcut = {
  checkedValue: number;
  amount: number;
  amountLabel: string;
  label: string;
}

const ButtonGroup = (props: {
  isChecked;
  shortcuts: AmountShortcut[];
  onClickShortcut: (shortcut: AmountShortcut) => void;
}) => {
  const {
    isChecked,
    shortcuts,
    onClickShortcut,
  } = props
  if (shortcuts.length === 0) {
    return null
  }

  return (
    <SpaceUI>
      {shortcuts.map((shortcut) => (
        <ButtonUI
          className={classNames([isChecked == shortcut.checkedValue ? 'isChecked' : ''], 'amountBtn')}
          key={shortcut.checkedValue}
          onClick={() => onClickShortcut(shortcut)}
        >
          {shortcut.label}
        </ButtonUI>
      ))}
    </SpaceUI>
  )
}

const InputWithdrawAmount = (props) => {
  const prefixCls = 'fym-InputWithdrawAmount'
  const { balance, min: minAmount, max: maxAmount, value,onChange , availableAmount , lockAmount } = props
  const { t } = useTranslationPlus('Wallet');
  const [inputValue, setInputValue] = useState('')
  const [isChecked, setIsChecked] = useState(0);
  const isLockAmountPositive = Number(lockAmount) > 0

  const { openAuditRecords } = useDialogManager()
  useEffect(() => {
    if(isChecked>0){

      setIsChecked(isChecked)

    }
  }, [isChecked])

  useEffect(()=>{
    if(!value){
      setIsChecked(0)
      setInputValue('')
    }
  },[value])

  const getNumberAmount = (amount) => {
    const numberAmount = Number(amount)
    return Number.isFinite(numberAmount) ? numberAmount : undefined
  }

  const formatAmountValue = (amount) => {
    let result:any = Math.floor(getNumberAmount(amount) ?? 0);
    result = result.toFixed(2);
    return result
  }

  const setAmountValue = (amount) => {
    const result = formatAmountValue(amount)
    setInputValue(result+'')
    onChange &&  onChange(result)
  }

  const getMaxWithdrawAmount = () => {
    const balanceAmount = getNumberAmount(balance)
    const maxWithdrawLimit = getNumberAmount(maxAmount)
    if (balanceAmount === undefined || maxWithdrawLimit === undefined) {
      return 0
    }

    const amountList = [balanceAmount, maxWithdrawLimit]
    if (availableAmount !== undefined) {
      const availableWithdrawAmount = getNumberAmount(availableAmount)
      if (availableWithdrawAmount === undefined) {
        return 0
      }
      amountList.push(availableWithdrawAmount)
    }

    return Math.min(...amountList)
  }

  const maxWithdrawAmount = getMaxWithdrawAmount()
  const minWithdrawLimit = getNumberAmount(minAmount)
  const hasValidWithdrawAmount =
    minWithdrawLimit !== undefined && maxWithdrawAmount >= minWithdrawLimit
  const effectiveMinWithdrawAmount =
    hasValidWithdrawAmount && minWithdrawLimit !== undefined ? minWithdrawLimit : 0
  const effectiveMaxWithdrawAmount = hasValidWithdrawAmount ? maxWithdrawAmount : 0
  const halfWithdrawAmount = hasValidWithdrawAmount
    ? Math.max(effectiveMinWithdrawAmount, effectiveMaxWithdrawAmount * 0.5)
    : 0
  const amountShortcuts = (() => {
    if (!hasValidWithdrawAmount) {
      return []
    }

    const shortcutList = [
      { checkedValue: 1, amount: effectiveMinWithdrawAmount },
      { checkedValue: 2, amount: halfWithdrawAmount },
      { checkedValue: 3, amount: effectiveMaxWithdrawAmount },
    ]
    const shownLabels = new Set<string>()

    return shortcutList.reduce<AmountShortcut[]>((result, shortcut) => {
      const label = formatAmountValue(shortcut.amount)
      if (label === '0.00' || shownLabels.has(label)) {
        return result
      }

      shownLabels.add(label)
      result.push({
        ...shortcut,
        amountLabel: label,
        label,
      })
      return result
    }, [])
  })()
  const displayAmountShortcuts = amountShortcuts.map((shortcut, index) => {
    if (amountShortcuts.length === 1) {
      return shortcut
    }

    if (amountShortcuts.length === 2) {
      return {
        ...shortcut,
        label: index === 0 ? t('Min') : t('All'),
      }
    }

    if (amountShortcuts.length === 3) {
      return {
        ...shortcut,
        label: [t('Min'), '50%', t('All')][index],
      }
    }

    return shortcut
  })

  const onInputChange = (v) => {
    const valueNum = Number(v);
    if (isNaN(valueNum)) {
      return v
    }
    const amValue = valueNum < effectiveMaxWithdrawAmount ? v : effectiveMaxWithdrawAmount
    setInputValue(Math.floor(Number(amValue)).toFixed(0)+'')
    onChange &&  onChange(amValue)
  }

  const onClickShortcut = (shortcut: AmountShortcut) => {
    setIsChecked(shortcut.checkedValue)
    setAmountValue(shortcut.amount)
  }
  return (
    <div className={prefixCls}>
     <InputUI
          className={`${prefixCls}-amount`}
          value={inputValue}
          maxLength={10}
          onChange={(v) => {
            onInputChange(v) }}
          suffix={displayAmountShortcuts.length > 0 ? (
            <ButtonGroup
              isChecked={isChecked}
              shortcuts={displayAmountShortcuts}
              onClickShortcut={onClickShortcut}
            />
          ) : null}
        />
       <div className="bootom-inner">
      { availableAmount !== undefined && <div>{t('AvailableAmount') + availableAmount}</div>}
       { lockAmount !== undefined &&  <div
         onClick={openAuditRecords}
         className={classNames('locak-amount', {
           'locak-amount-positive': isLockAmountPositive,
         })}
       >{t('LockAmount') + lockAmount}</div>}
       </div>
    </div>
  )
}
export default InputWithdrawAmount
